import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ImageOff, MapPin, Truck } from "lucide-react";
import type {
  Address,
  OrderLineItem,
  OrderWithDetails,
  Shipment,
} from "@estelle/domain";
import { useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { PhoneNumber } from "@/components/phone/PhoneNumber";
import { EmptyState, LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import { reportingNow } from "@/lib/reportClock";
import { safeWebHref } from "@/lib/safe-href";
import { cn } from "@/lib/utils";
import { CommentThread } from "@/modules/comments/CommentThread";
import { CustomerLink } from "@/modules/contacts/customers/screens/CustomerLink";
import {
  cancelReasonLabel,
  cancelledBy as cancelActor,
} from "@/modules/orders/cancel";
import { dateTime, money } from "@/modules/orders/format";
import { AddReturnAction } from "@/modules/orders/returns/AddReturnAction";
import { OrderCancelAction } from "@/modules/orders/screens/OrderCancelAction";
import { plain } from "@/modules/orders/plain";
import { awaitsConfirmationCall, useOrderCalls } from "@/modules/orders/calls";
import {
  CallOutcomePrompt,
  CallSummaryLine,
} from "@/modules/orders/screens/OrderCalls";
import { OrderFacts } from "@/modules/orders/screens/OrderFacts";
import { OrderHistory } from "@/modules/orders/screens/OrderHistory";
import { OrderPayments } from "@/modules/orders/screens/OrderPayments";
import {
  OrderStageHint,
  OrderStageHistory,
} from "@/modules/orders/screens/OrderStageCard";
import { OrderReturns } from "@/modules/orders/screens/OrderReturns";
import { OrderRawPayload } from "@/modules/orders/screens/OrderRawPayload";
import { ShipmentTracking } from "@/modules/orders/screens/ShipmentTracking";
import { ViewJourneyLink } from "@/modules/orders/screens/OrderJourney";
import { ManualMarker, StagePill } from "@/modules/orders/screens/StageParts";
import {
  OrderNoteEditor,
  OrderTagsEditor,
} from "@/modules/orders/screens/OrderTagsAndNote";
import {
  SHOPIFY_STATUS_LABEL,
  exactIndiaTime,
  timeAgo,
} from "@/modules/orders/tracking";
import { useRefreshTracking } from "@/modules/orders/useRefreshTracking";
import { LinkedTasks } from "@/modules/tasks/components/LinkedTasks";

/* ------------------------------------------------------------ raw readers */

/**
 * Fields the order page shows that have no column — the shipping method,
 * risk, what each shipment carried. Read from the channel payload without
 * trusting its shape; fixture orders carry none of them and show none.
 */
function rawOf(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : {};
}

function edges(value: unknown): Record<string, unknown>[] {
  const list = rawOf(value).edges;
  return Array.isArray(list) ? list.map((edge) => rawOf(rawOf(edge).node)) : [];
}

function text(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function shippingMethod(order: OrderWithDetails): string | null {
  return text(edges(rawOf(order.raw).shippingLines)[0]?.title);
}

/** Line id → quantity one fulfilment carried. */
function shippedQuantities(shipment: Shipment): Map<string, number> {
  const out = new Map<string, number>();
  for (const node of edges(rawOf(shipment.raw).fulfillmentLineItems)) {
    const lineId = text(rawOf(node.lineItem).id);
    const quantity = typeof node.quantity === "number" ? node.quantity : 0;
    if (lineId) out.set(lineId, (out.get(lineId) ?? 0) + quantity);
  }
  return out;
}

/* ----------------------------------------------------------------- pieces */

type Tone = "warning" | "success" | "neutral" | "danger";

function StatusBadge({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <Badge
      variant="secondary"
      className={cn(
        "gap-1.5 font-normal",
        tone === "warning" && "bg-tint-yellow text-tint-yellow-foreground",
        tone === "success" && "bg-tint-green text-tint-green-foreground",
        tone === "danger" && "bg-tint-red text-tint-red-foreground",
        tone === "neutral" && "bg-tint-gray text-tint-gray-foreground",
      )}
    >
      <span aria-hidden className="size-1.5 rounded-full bg-current" />
      {children}
    </Badge>
  );
}

function ItemRow({
  item,
  quantity,
}: {
  item: OrderLineItem;
  quantity: number;
}) {
  return (
    <li className="flex items-center gap-3 py-2">
      {item.imageUrl ? (
        <img
          src={item.imageUrl}
          alt=""
          loading="lazy"
          className="size-12 shrink-0 rounded-md border object-cover"
        />
      ) : (
        <div
          aria-hidden
          className="bg-muted text-muted-foreground flex size-12 shrink-0 items-center justify-center rounded-md border"
        >
          <ImageOff className="size-4" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{item.title}</p>
        {item.sku ? (
          <p className="text-muted-foreground text-xs">SKU {item.sku}</p>
        ) : null}
        {/* Under the title on a phone. Three columns of numbers beside a
            thumbnail leaves the title a few characters wide. */}
        <p className="text-muted-foreground text-xs tabular-nums sm:hidden">
          {money(item.unitPrice)} × {quantity}
        </p>
      </div>
      <p className="text-muted-foreground hidden shrink-0 text-sm tabular-nums sm:block">
        {money(item.unitPrice)} × {quantity}
      </p>
      <p className="w-20 shrink-0 text-right text-sm tabular-nums sm:w-24">
        {money(item.lineTotal)}
      </p>
    </li>
  );
}

/** The shipping method and the location, above a group of items. */
function ShipBar({
  method,
  location,
  children,
}: {
  method: ReactNode;
  location: string | null;
  children?: ReactNode;
}) {
  return (
    <div className="bg-muted/40 flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
      <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="flex items-center gap-2">
          <Truck className="text-muted-foreground size-4" aria-hidden />
          {method}
        </span>
        {children}
      </span>
      {location ? (
        <span className="text-muted-foreground flex items-center gap-1 text-xs">
          <MapPin className="size-3.5" aria-hidden />
          {location}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Items grouped the way Shopify groups them: one card per shipment, and one
 * for what has not shipped — with the shipping method and the location it
 * ships from, which is what someone packing it needs.
 */
function FulfilmentCards({ order }: { order: OrderWithDetails }) {
  // One for the order, shared by its shipment cards: the server refreshes and
  // rate-limits the order as a whole (#527).
  const refresh = useRefreshTracking(order.id);
  const shipped = new Map<string, number>();
  for (const shipment of order.shipments) {
    if (shipment.status === "cancelled") continue;
    for (const [lineId, quantity] of shippedQuantities(shipment)) {
      shipped.set(lineId, (shipped.get(lineId) ?? 0) + quantity);
    }
  }
  const byExternal = new Map(
    order.lineItems.map((line) => [line.externalId, line]),
  );
  const unfulfilled = order.lineItems
    .map((line) => {
      const current = rawOf(line.raw).currentQuantity;
      const remaining =
        (typeof current === "number" ? current : line.quantity) -
        (shipped.get(line.externalId) ?? 0);
      return { line, quantity: remaining };
    })
    .filter((entry) => entry.quantity > 0);
  const method = shippingMethod(order);

  return (
    <div className="space-y-4">
      {unfulfilled.length > 0 && !order.cancelledAt ? (
        <Card className="gap-3">
          <CardHeader>
            <div>
              <StatusBadge tone="warning">Unfulfilled</StatusBadge>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {/* A missing method is a quiet note in the facts strip now, not a
                bar that looks like an action (#644). */}
            {method || order.warehouseName ? (
              <ShipBar
                method={method ?? "Shipping method not set"}
                location={order.warehouseName}
              />
            ) : null}
            <ul className="divide-y">
              {unfulfilled.map(({ line, quantity }) => (
                <ItemRow key={line.id} item={line} quantity={quantity} />
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {order.shipments.map((shipment) => {
        const lines = [...shippedQuantities(shipment)].flatMap(
          ([lineId, quantity]) => {
            const line = byExternal.get(lineId);
            return line ? [{ line, quantity }] : [];
          },
        );
        const status = shipment.status;
        // `null` for anything that is not http or https: the address comes
        // from the channel as written, and is shown as text rather than
        // followed (#501).
        const trackingHref = safeWebHref(shipment.trackingUrl);
        return (
          <Card key={shipment.id} className="gap-3">
            <CardHeader>
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge
                  tone={
                    status === "delivered"
                      ? "success"
                      : status === "cancelled"
                        ? "danger"
                        : status === "not_delivered" ||
                            status === "attempted_delivery"
                          ? "warning"
                          : "neutral"
                  }
                >
                  {status ? SHOPIFY_STATUS_LABEL[status] : "Fulfilled"}
                </StatusBadge>
                <span className="text-muted-foreground text-sm">
                  {shipment.name}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <ShipBar
                method={shipment.courier ?? "Courier not set"}
                location={text(rawOf(rawOf(shipment.raw).location).name)}
              >
                {shipment.trackingNumber ? (
                  trackingHref ? (
                    <a
                      href={trackingHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-strong tabular-nums underline-offset-2 hover:underline"
                    >
                      {shipment.trackingNumber}
                    </a>
                  ) : (
                    <span className="tabular-nums">
                      {shipment.trackingNumber}
                    </span>
                  )
                ) : (
                  <span className="text-muted-foreground">
                    No tracking number
                  </span>
                )}
                <span className="text-muted-foreground text-xs">
                  Shipped {dateTime(shipment.shippedAt)}
                </span>
                {shipment.deliveredAt ? (
                  <span className="text-muted-foreground text-xs">
                    Delivered {dateTime(shipment.deliveredAt)}
                  </span>
                ) : null}
              </ShipBar>
              <ShipmentTracking shipment={shipment} refresh={refresh} />
              {lines.length > 0 ? (
                <ul className="divide-y">
                  {lines.map(({ line, quantity }) => (
                    <ItemRow key={line.id} item={line} quantity={quantity} />
                  ))}
                </ul>
              ) : null}
            </CardContent>
          </Card>
        );
      })}

      {(unfulfilled.length === 0 || order.cancelledAt) &&
      order.shipments.length === 0 ? (
        <Card className="gap-3">
          <CardHeader>
            <CardTitle className="text-sm">Items</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y">
              {order.lineItems.map((line) => (
                <ItemRow key={line.id} item={line} quantity={line.quantity} />
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function SideCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="gap-3 py-4">
      <CardHeader className="px-4">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 px-4 text-sm">{children}</CardContent>
    </Card>
  );
}

function addressLines(address: Address): string[] {
  const cityRow = [
    address.zip,
    address.city,
    address.provinceCode ?? address.province,
  ]
    .filter((part): part is string => Boolean(part && part.trim() !== ""))
    .join(" ");
  return [
    address.name,
    address.company,
    address.line1,
    address.line2,
    cityRow,
    address.country,
  ].filter((part): part is string => Boolean(part && part.trim() !== ""));
}

function sameAddress(a: Address | null, b: Address | null): boolean {
  if (!a || !b) return false;
  return addressLines(a).join("|") === addressLines(b).join("|");
}

function Sidebar({ order }: { order: OrderWithDetails }) {
  const permissions = usePermissions();
  const risk = rawOf(rawOf(order.raw).risk);
  const assessments = Array.isArray(risk.assessments)
    ? risk.assessments.map(rawOf)
    : [];
  const facts = assessments
    .flatMap((a) => (Array.isArray(a.facts) ? a.facts.map(rawOf) : []))
    .map((fact) => text(fact.description))
    .filter((fact): fact is string => fact !== null);
  const recommendation = text(risk.recommendation);

  return (
    <div className="space-y-4">
      <SideCard title="Notes">
        <OrderNoteEditor
          orderId={order.id}
          channelUpdatedAt={order.channelUpdatedAt}
          note={order.note}
          canEdit={can(permissions, "orders.detail.editNote")}
        />
      </SideCard>

      {order.noteAttributes.length > 0 ? (
        <SideCard title="Additional details">
          <dl className="space-y-2">
            {order.noteAttributes.map((attribute) => (
              <div key={attribute.name}>
                <dt className="text-muted-foreground text-xs">
                  {attribute.name}
                </dt>
                <dd className="break-words">{attribute.value || "—"}</dd>
              </div>
            ))}
          </dl>
        </SideCard>
      ) : null}

      <SideCard title="Channel">
        <p>{order.sourceApp ?? "—"}</p>
      </SideCard>

      <SideCard title="Customer">
        {order.customer ? (
          <>
            <p className="font-medium">
              <CustomerLink customerId={order.customer.id}>
                {order.customer.displayName}
              </CustomerLink>
            </p>
            {order.customer.ordersCount !== null ||
            order.customer.amountSpent !== null ? (
              <p className="text-muted-foreground">
                {order.customer.ordersCount !== null ? (
                  <>
                    {order.customer.ordersCount} order
                    {order.customer.ordersCount === 1 ? "" : "s"}
                  </>
                ) : null}
                {order.customer.ordersCount !== null &&
                order.customer.amountSpent !== null
                  ? " · "
                  : null}
                {order.customer.amountSpent !== null ? (
                  <>{money(order.customer.amountSpent)} spent</>
                ) : null}
              </p>
            ) : null}
            <p className="text-muted-foreground pt-3 text-xs font-medium">
              Contact information
            </p>
            {order.customer.email ? (
              <p className="break-all">{order.customer.email}</p>
            ) : null}
            {order.customer.phone ? (
              <p>
                <PhoneNumber
                  number={order.customer.phone}
                  name={order.customer.displayName}
                />
              </p>
            ) : null}
          </>
        ) : (
          <p className="text-muted-foreground">
            No customer — a guest checkout.
          </p>
        )}

        <p className="text-muted-foreground pt-3 text-xs font-medium">
          Shipping address
        </p>
        {order.shippingAddress ? (
          <>
            {addressLines(order.shippingAddress).map((line) => (
              <p key={line}>{line}</p>
            ))}
            {order.shippingAddress.phone ? (
              <p>
                <PhoneNumber
                  number={order.shippingAddress.phone}
                  name={order.shippingAddress.name}
                />
              </p>
            ) : null}
          </>
        ) : (
          <p className="text-muted-foreground">Not set</p>
        )}

        <p className="text-muted-foreground pt-3 text-xs font-medium">
          Billing address
        </p>
        {!order.billingAddress ||
        sameAddress(order.billingAddress, order.shippingAddress) ? (
          <p className="text-muted-foreground">Same as shipping address</p>
        ) : (
          addressLines(order.billingAddress).map((line) => (
            <p key={line}>{line}</p>
          ))
        )}
      </SideCard>

      <SideCard title="Order risk">
        {recommendation && recommendation !== "NONE" ? (
          <p>
            Recommendation:{" "}
            <span className="font-medium capitalize">
              {recommendation.toLowerCase()}
            </span>
          </p>
        ) : null}
        {facts.length > 0 ? (
          facts.map((fact) => (
            <p key={fact} className="text-muted-foreground text-xs">
              {fact}
            </p>
          ))
        ) : (
          <p className="text-muted-foreground">Analysis not available</p>
        )}
      </SideCard>

      <SideCard title="Tags">
        <OrderTagsEditor
          orderId={order.id}
          channelUpdatedAt={order.channelUpdatedAt}
          tags={order.tags}
          canEdit={can(permissions, "orders.detail.editTags")}
        />
      </SideCard>
    </div>
  );
}

type DetailTab = "items" | "payment" | "timeline" | "notes" | "comments";

/**
 * One order, read top down (#644): the title with the stage once, the stage's
 * hint and its next action, the four facts in a strip, then tabs for the rest
 * — Items, Payment, Timeline, Notes, Comments.
 *
 * The facts used to be spread over three cards of equal weight, the phone and
 * address in a small card below Notes and Channel, and on a phone the customer
 * started about 1,000 px down. The header stays pinned while the tabs scroll;
 * on a phone the stage's action sits in a bar at the bottom of the screen.
 *
 * Tags, the note and cancelling write through to Shopify (#164) for anyone
 * holding the matching `orders.detail.*` permission; for everyone else they
 * read as before. The API checks the same permissions itself.
 */
function OrderDetailBody({ order }: { order: OrderWithDetails }) {
  const permissions = usePermissions();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState<DetailTab>("items");
  // Where a phone puts the stage's action: the bar at the foot of the screen.
  const [actionBar, setActionBar] = useState<HTMLDivElement | null>(null);
  // Confirmation calls (#636): logged on an order awaiting one.
  const calls = useOrderCalls(order.id, awaitsConfirmationCall(order));
  const onCall = calls.enabled
    ? async () => {
        const result = await calls.record.mutateAsync(null);
        if (!result.ok) throw new Error(result.rejection.message);
      }
    : undefined;
  const cancelledBy = cancelActor(order.channelEvents, plain);
  // Add a return (#594): a parcel that came back unmarked, on an order that stands.
  const showAddReturn =
    can(permissions, "orders.returns.receive") &&
    order.cancelledAt === null &&
    order.shipments.length > 0;
  const noteCount = (order.note ? 1 : 0) + order.noteAttributes.length;

  const tabs: { id: DetailTab; label: string; count?: number }[] = [
    { id: "items", label: "Items" },
    { id: "payment", label: "Payment" },
    { id: "timeline", label: "Timeline" },
    { id: "notes", label: "Notes", count: noteCount },
    { id: "comments", label: "Comments", count: order.commentCount },
  ];

  return (
    <div className="space-y-4">
      <header className="bg-background sticky top-0 z-20 -mx-3 -mt-3 space-y-2 border-b px-3 pt-3 pb-3 sm:-mx-4 sm:-mt-4 sm:px-4">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <h2 className="text-lg font-semibold tabular-nums">
            {order.orderNumber}
          </h2>
          <StagePill stage={order.stage} />
          {order.stageOverride ? (
            <ManualMarker override={order.stageOverride} />
          ) : null}
          <span
            className="text-muted-foreground text-sm"
            title={exactIndiaTime(order.placedAt)}
          >
            Placed {timeAgo(order.placedAt, reportingNow())}
          </span>
          <ViewJourneyLink order={order} />
          {order.cancelledAt ? (
            <StatusBadge tone="danger">Cancelled</StatusBadge>
          ) : null}
          {order.rtoReceived ? (
            <StatusBadge tone="success">RTO received, confirmed</StatusBadge>
          ) : null}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {showAddReturn ? (
              <AddReturnAction orderNumber={order.orderNumber} />
            ) : null}
            {can(permissions, "orders.detail.cancel") ? (
              <OrderCancelAction
                orderId={order.id}
                orderNumber={order.orderNumber}
                channelUpdatedAt={order.channelUpdatedAt}
                cancelledAt={order.cancelledAt}
                rtoReceived={order.rtoReceived}
              />
            ) : null}
          </div>
        </div>
        <OrderStageHint
          order={order}
          renderActions={
            isMobile
              ? (actions) =>
                  actionBar ? createPortal(actions, actionBar) : null
              : undefined
          }
        />
      </header>

      {order.cancelledAt ? (
        <p className="text-destructive text-sm">
          Cancelled {dateTime(order.cancelledAt)}
          {cancelledBy ? ` · ${cancelledBy}` : ""}
          {order.cancelReason
            ? ` · Reason: ${cancelReasonLabel(order.cancelReason)}`
            : ""}
        </p>
      ) : null}

      <CallOutcomePrompt calls={calls} />

      <OrderFacts
        order={order}
        shippingMethod={shippingMethod(order)}
        onCall={onCall}
        callSlot={calls.enabled ? <CallSummaryLine calls={calls} /> : undefined}
      />

      <div>
        <div
          role="tablist"
          aria-label="Order sections"
          className="-mx-3 flex gap-1 overflow-x-auto border-b px-3 [scrollbar-width:none] sm:-mx-4 sm:px-4"
        >
          {tabs.map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="tab"
              id={`order-tab-${entry.id}`}
              aria-selected={tab === entry.id}
              aria-controls={`order-panel-${entry.id}`}
              onClick={() => {
                setTab(entry.id);
              }}
              className={cn(
                "-mb-px inline-flex min-h-11 shrink-0 items-center gap-1.5 border-b-2 px-3 text-sm whitespace-nowrap transition-colors md:min-h-10",
                tab === entry.id
                  ? "border-primary text-foreground font-semibold"
                  : "text-muted-foreground hover:text-foreground border-transparent",
              )}
            >
              {entry.label}
              {entry.count ? (
                <span className="bg-muted text-muted-foreground rounded-full px-1.5 text-xs tabular-nums">
                  {entry.count}
                </span>
              ) : null}
            </button>
          ))}
        </div>

        <div
          role="tabpanel"
          id={`order-panel-${tab}`}
          aria-labelledby={`order-tab-${tab}`}
          className="space-y-4 pt-4"
        >
          {tab === "items" ? (
            <>
              <FulfilmentCards order={order} />
              <OrderReturns receipts={order.returnReceipts} />
            </>
          ) : tab === "payment" ? (
            <OrderPayments order={order} />
          ) : tab === "timeline" ? (
            <>
              <OrderStageHistory order={order} className="bg-background" />
              <OrderHistory
                orderId={order.id}
                channelEvents={order.channelEvents}
              />
              <OrderRawPayload raw={order.raw} />
            </>
          ) : tab === "notes" ? (
            <Sidebar order={order} />
          ) : (
            <>
              <CommentThread entityType="order" entityId={order.id} />
              <LinkedTasks
                targetType="order"
                targetId={order.id}
                followUpTitle={`Follow up order ${order.orderNumber}`}
              />
            </>
          )}
        </div>
      </div>

      {/* The phone's action bar: filled from the stage hint, above. */}
      {isMobile ? (
        <div
          ref={setActionBar}
          className="bg-background sticky bottom-0 z-20 -mx-3 -mb-3 flex gap-2 border-t px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] empty:hidden"
        />
      ) : null}
    </div>
  );
}

/**
 * One order, opened over the list in a drawer (M2-11).
 *
 * `onBack` is optional because the drawer already carries a close control.
 */
export function OrderDetail({
  orderId,
  onBack,
}: {
  orderId: string;
  onBack?: () => void;
}) {
  const data = useDataSource();

  const query = useQuery({
    queryKey: ["orders", "detail", orderId],
    queryFn: () => data.orders.get(orderId),
    // A receipt another request is still writing to Shopify is read again until
    // it is complete, refused or stale, so the page never keeps a mid-write
    // moment on screen as if it were the outcome (#558).
    refetchInterval: (current) =>
      current.state.data?.returnReceipts.some((receipt) => receipt.processing)
        ? 1500
        : false,
  });

  return (
    <div className="flex h-full min-h-0 flex-col">
      {onBack ? (
        <div className="shrink-0 border-b px-4 py-2">
          <Button size="sm" variant="ghost" onClick={onBack}>
            <ArrowLeft />
            Back to list
          </Button>
        </div>
      ) : null}

      <div className="bg-muted/30 min-h-0 flex-1 overflow-auto p-3 sm:p-4">
        {query.isPending ? (
          <TableSkeleton rows={4} />
        ) : query.isError ? (
          <LoadFailed
            message={query.error.message}
            onRetry={() => {
              void query.refetch();
            }}
          />
        ) : query.data === null ? (
          <EmptyState
            title="Order not found"
            description="It may have been deleted since this list was loaded."
          />
        ) : (
          <OrderDetailBody order={query.data} />
        )}
      </div>
    </div>
  );
}
